import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { EVENT_TRACER } from '../../../constants';
import { reportEvent } from '../../../utils';
import LoaderLulo from '../../../components/loaderLulo';
import LoaderIOS from '../../../components/loaderIOS';
import { useLivenessConfig } from './hooks/useLivenessConfig';
import { useResponsiveStyles } from './hooks/useResponsiveStyles';
import { useWebSocketLiveness } from './hooks/useWebSocketLiveness';
import {SDK_EVENT_KEYS} from "../../../utils/logService";
import {useNavigate} from "react-router-dom";
import checkicon from '../../../assets/image/lulo/check.png'

const CAMERA_STATES = {
  CHECKING_CAMERA: 'CHECKING_CAMERA',
  READY: 'READY',
  STABLE_OK: 'STABLE_OK',
  CAPTURING: 'CAPTURING',
  VALIDATING_BUNDLE: 'VALIDATING_BUNDLE',
  UPLOADING_VIDEO: 'UPLOADING_VIDEO',
  PROCESSING_RESPONSE: 'PROCESSING_RESPONSE',
  VALIDATION_COMPLETE: 'VALIDATION_COMPLETE',
  PASS: 'PASS',
  FAIL: 'FAIL',
  PERMISSION_DENIED: 'PERMISSION_DENIED',
  AUTO_CLOSE: 'AUTO_CLOSE',
  CANCELLED: 'CANCELLED',
};

const timestamp = () => new Date().toISOString();

const normalizeDetectionPayload = (payload) => {
  const collected = [];
  const pushStrings = (incoming) => {
    if (!incoming) return;
    if (Array.isArray(incoming)) {
      incoming.forEach((value) => {
        if (typeof value === 'string') collected.push(value);
      });
    }
  };
  if (Array.isArray(payload)) {
    pushStrings(payload);
  } else if (payload && typeof payload === 'object') {
    const bag = payload;
    pushStrings(bag.errors);
    pushStrings(bag.warnings);
    pushStrings(bag.codes);
  }
  return collected;
};

const formatLogLabel = (processId, label) => `[${timestamp()}][${processId}] ${label}`;

const serializeDetail = (value) => {
  if (value === undefined || value === null) return '';
  if (typeof value === 'string') return value;
  try {
    return JSON.stringify(value, null, 2);
  } catch (error) {
    return String(value);
  }
};

const writeFileToDirectory = async (directory, fileName, data) => {
  const fileHandle = await directory.getFileHandle(fileName, { create: true });
  const writable = await fileHandle.createWritable();
  if (typeof data === 'string') {
    await writable.write(new Blob([data], { type: 'text/plain' }));
  } else {
    await writable.write(data);
  }
  await writable.close();
};

const blobToBase64 = async (blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const result = typeof reader.result === 'string' ? reader.result : '';
      const base64 = result.includes(',') ? result.split(',')[1] ?? '' : result;
      resolve(base64);
    };
    reader.onerror = () => reject(reader.error ?? new Error('FileReader error'));
    reader.readAsDataURL(blob);
  });

const pickMediaRecorderMimeType = () => {
  if (typeof MediaRecorder === 'undefined') return undefined;
  const codecsToCheck = [
    'video/webm; codecs=vp8',
    'video/webm; codecs=vp9',
    'video/webm; codecs=vp10',
    'video/webm; codecs=h264',
    'video/webm; codecs="vp8, opus"',
    'video/webm; codecs="vp9, opus"',
    'video/webm; codecs="vp8, vorbis"',
    'video/webm; codecs="vp9, vorbis"',
    'video/mp4; codecs=avc1.42E01E',
    'video/mp4; codecs=avc1.58A01E',
    'video/mp4; codecs=avc1.4D401E',
    'video/mp4; codecs=avc1.64001E',
    'video/mp4; codecs=hevc',
    'video/mp4; codecs="avc1.42E01E, mp4a.40.2"',
    'video/mp4; codecs="avc1.4D401E, mp4a.40.2"',
    'video/mp4; codecs="avc1.64001E, mp4a.40.2"',
    'video/mp4',
    'video/ogg; codecs=theora',
    'video/ogg; codecs="theora, vorbis"',
    'video/ogg; codecs="theora, opus"',
    'video/av1',
    'video/av1; codecs="av01"',
    'video/vp8',
    'video/vp9',
    'video/3gpp',
    'video/3gpp; codecs="mp4v.20.8, samr"',
    'video/h265',
    'video/x-matroska; codecs=avc1',
    'video/x-matroska; codecs="avc1, opus"',
    'video/x-matroska; codecs="avc1, vorbis"',
    'video/mp2t',
    'video/mp2t; codecs="avc1.42E01E, mp4a.40.2"',
    'video/mp2t; codecs="avc1.4D401E, mp4a.40.2"',
    'video/quicktime',
    'video/mpeg',
    'video/webm',
  ];
  for (const codec of codecsToCheck) {
    try {
      if (!MediaRecorder.isTypeSupported || MediaRecorder.isTypeSupported(codec)) {
        return codec;
      }
    } catch (_) {}
  }
  return undefined;
};

export default function LivenessV7({ configValue, configLoading }) {
  const wcRef = useRef(null);
  const mediaStreamRef = useRef(null);
  const customVideoRef = useRef(null);
  const cameraMounted = useRef(false);

    const [message, setMessage] = useState('');
    const streamRef = useRef(null);
    const [borderColor, setBorderColor] = useState('#fff');
    const [recordingVideo, setRecordingVideo] = useState(false)
    const [loadingMetaData, setLoadingMetaData] = useState(false)
    const [showcheck, setShowCheck] = useState(false)
    const [loading, setLoading] = useState(true)
  const {
    config,
    styles: configStyles,
    timing,
    sdkConfig,
    cameraConfig,
    validations,
    getMessages,
  } = useLivenessConfig(configValue, configLoading);

  /*
  useEffect(() => {
      setBorderColor(configValue?.styles.successColor)
  }, [])*/
  const responsiveStyles = useResponsiveStyles(configStyles);

  const OK_FRAMES_TARGET = timing.okFramesTarget;
  const PASS_PROBABILITY_THRESHOLD = timing.passProbabilityThreshold;
  const AUTO_CLOSE_DELAY_MS = timing.autoCloseDelayMs;

  const [cameraState, setCameraState] = useState(CAMERA_STATES.CHECKING_CAMERA);
  const [okFrameCount, setOkFrameCount] = useState(0);
  const [isCapturing, setIsCapturing] = useState(false);
  const [messagesLiveness, setMessagesLiveness] = useState('');
  const [, setSubtitle] = useState('');
  const [detectionStatus, setDetectionStatus] = useState('OK');
  const [finalResult, setFinalResult] = useState(null);
  const [isLibraryVisible, setIsLibraryVisible] = useState(false);

  const params = useMemo(
    () =>
      new Proxy(new URLSearchParams(window.location.search), {
        get: (searchParams, prop) => searchParams.get(prop),
      }),
    []
  );

  const urlServiceLiveness = params.urlServiceLiveness;
  const projectName = params.projectName;
  const codeTransaction = params.codeTransaction;
  const processKey = codeTransaction;
  const urlTracer = params.urlTracer;
  const language = params.language || 'es';
    const emitReadyCam = params.emitReadyCam || 'false'
  const platform = params.plataform || params.platform || 'web';
  const isMovil = params.isMovil || false;
  const isLuloProject = projectName?.toLowerCase().includes('lulo');
    const isManager = params.isManager || 'false'
    const navigate = useNavigate()



  const processId = useMemo(
    () =>
      typeof crypto !== 'undefined' && 'randomUUID' in crypto
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(16).slice(2, 10)}`,
    []
  );

  const isCapturingRef = useRef(isCapturing);
  const okFrameCountRef = useRef(okFrameCount);
  const closingTimeoutRef = useRef(null);
  const autoCapturePendingRef = useRef(false);
  const hasOpenedCameraRef = useRef(false);
  const sessionStartedAtRef = useRef(new Date().toISOString());
  const captureDirectoryHandleRef = useRef(null);
  const artifactsSavedRef = useRef(false);
  const eventLogRef = useRef([]);
  const lastDetectionCodesRef = useRef([]);
  const lastVideoInfoRef = useRef({});
  const mediaRecorderRef = useRef(null);
  const recordedChunksRef = useRef([]);
  const recordingStartedAtRef = useRef(null);
  const recordingMimeTypeRef = useRef('video/webm');
  const recordingStopPromiseRef = useRef(null);
  const recordingStopResolverRef = useRef(null);
  const livenessVideoBase64Ref = useRef(null);
  const livenessVideoBlobRef = useRef(null);
  const finalResultCapturedRef = useRef(false);
  const validationResultRef = useRef(null);
  const backgroundResultReadyRef = useRef(false);

  useEffect(() => {
    isCapturingRef.current = isCapturing;
  }, [isCapturing]);

  useEffect(() => {
    okFrameCountRef.current = okFrameCount;
  }, [okFrameCount]);

  const onReportEvent = useCallback(
    async (eventType, message = '') => {
      if (urlTracer && processId) {
        try {
          await reportEvent(urlTracer, {
            eventType,
            message,
            processId,
            projectName,
            timestamp: new Date().toISOString(),
          });
        } catch (_) {}
      }
    },
    [urlTracer, processId, projectName]
  );

  const responseToHost = useCallback(
    (response, responseStatus = 'success') => {
        console.log('llego acaaaa', response, responseStatus)
      const payload = {
        origin: 'liveness',
        type: 'success',
        step: 'success',
        data: response,
      };
      if (platform === 'ios') {
        window.webkit?.messageHandlers?.messageHandlersName?.postMessage(JSON.stringify(payload));
        return;
      }
      if (platform === 'android') {
        window.ResponseAdo?.handleEvent(JSON.stringify(payload));
        return;
      }
      if (platform === 'react-native' || isMovil) {
        window.ReactNativeWebView?.postMessage(JSON.stringify(payload));
        return;
      }
      window.parent.postMessage(JSON.stringify(payload), '*');
    },
    [platform, isMovil]
  );
    function goToManager() {
        if (isManager === 'true') {
            navigate(-1)
        }
    }

    function responseToHostError(response) {
        console.log('Llego al error emit error', response);
        onReportEvent(SDK_EVENT_KEYS.SendToHost).catch()
        // onReportEvent(EVENT_TRACER.LIVENESS.LivenessResponseToHost, "EL liveness emitió al host un error").catch()
        if (isMovil) {
            window.ReactNativeWebView.postMessage(
                JSON.stringify({
                    type: 'error-intern',
                    step: 'permissionDenied',
                    data: 'permissionDenied',
                    message: response.message,
                })
            );
            goToManager()
            return
        }
        if (platform === 'ios') {
            window.webkit.messageHandlers.messageHandlersName.postMessage(JSON.stringify({
                type: 'error-intern',
                step: 'permissionDenied',
                data: 'permissionDenied',
                message: response.message,
            }));
            goToManager()
            return
        }

        if (platform === 'android') {
            window.ResponseAdo.handleEvent(JSON.stringify({
                type: 'error-intern',
                step: 'permissionDenied',
                data: 'permissionDenied',
                message: response.message,
            }));
            goToManager()
            return
        }

        if (platform === 'react-native') {
            window.ReactNativeWebView.postMessage(JSON.stringify({
                type: 'error-intern',
                step: 'permissionDenied',
                data: 'permissionDenied',
                message: response.message,
            }))
            goToManager()
            return
        }
        if (platform === 'web') {
            window.parent.postMessage(JSON.stringify({
                origin: 'liveness',
                type: 'error-intern',
                step: 'permissionDenied',
                data: 'permissionDenied',
                message: response.message,
            }), '*')
            goToManager()
        }
    }

  const appendLog = useCallback(
    (level, label, detail) => {
      const base = formatLogLabel(processId, label);
      const detailString = detail !== undefined ? serializeDetail(detail) : '';
      const entry = `[${level}] ${base}${detailString ? ` ${detailString}` : ''}`;
      eventLogRef.current.push(entry);
    },
    [processId]
  );

  const logEvent = useCallback((label, detail) => appendLog('INFO', label, detail), [appendLog]);
  const logError = useCallback((label, detail) => appendLog('ERROR', label, detail), [appendLog]);

  const getMessageFromConfig = useCallback(
    (errorCode) => {
      const messages = getMessages(language);
      const result = messages[errorCode] || errorCode;
      return result;
    },
    [getMessages, language]
  );

  const ensureCameraReady = useCallback(() => {
    setCameraState(CAMERA_STATES.CHECKING_CAMERA);
    setMessagesLiveness('');
  }, []);

  const triggerAutoCapture = useCallback(() => {
    if (autoCapturePendingRef.current) return;
    const element = wcRef.current;
    if (!element?.takePhoto) return;
    autoCapturePendingRef.current = true;
    setIsCapturing(true);
    logEvent('autoCapture.triggered', { okFrameCount: okFrameCountRef.current });
    onReportEvent(EVENT_TRACER.LIVENESS.LivenessCaptureSuccess, 'Auto capture triggered');
    try {
      const maybePromise = element.takePhoto();
      if (maybePromise instanceof Promise) {
        maybePromise.catch(() => {
          setIsCapturing(false);
          autoCapturePendingRef.current = false;
        });
      }
    } catch (_) {
      setIsCapturing(false);
      autoCapturePendingRef.current = false;
    }
  }, [logEvent, onReportEvent]);

  const deriveStatus = useCallback((codes) => {
    if (!codes.length) return 'OK';

    const codeMap = {
      'FACE_IS_OCCLUDED': 'FACE_OCCLUDED',
      'POOR_LIGHTING': 'DARK_IMAGE',
      'BLURRY_IMAGE': 'FACE_BLURRY',
      'FACE_HORIZONTAL_MISALIGNMENT': 'FACE_NOT_CENTERED',
      'FACE_VERTICAL_MISALIGNMENT': 'FACE_NOT_CENTERED',
      'FACE_ROTATION_MISALIGNMENT': 'FACE_NOT_CENTERED',
      'FACE_NEVER_DETECTED': 'FACE_NOT_FOUND',
    };

    const normalizedCodes = codes.map(code => codeMap[code] || code);

    const errorPriority = [
      'FACE_CLOSE_TO_BORDER',
      'FACE_NOT_CENTERED',
      'FACE_TOO_SMALL',
      'FACE_TOO_CLOSE',
      'FACE_ANGLE_TOO_LARGE',
      'EYES_CLOSED',
      'PROBABILITY_TOO_SMALL',
      'DARK_IMAGE',
      'FACE_BLURRY',
      'TOO_MANY_FACES',
      'FACE_OCCLUDED',
      'SUNGLASSES_DETECTED',
      'FACE_NOT_FOUND',
      'FACE_CROPPED',
      'MOUTH_OPEN',
    ];

    for (const errorType of errorPriority) {
      if (normalizedCodes.includes(errorType)) return errorType;
    }

    const firstCode = normalizedCodes[0];
    return firstCode || 'GENERAL_ERROR';
  }, []);

  const updateProgress = useCallback(
    (codes) => {
      if (codes.length === 0) {
        const newCount = Math.min(okFrameCount + 1, OK_FRAMES_TARGET);
        setOkFrameCount(newCount);
        if (newCount >= OK_FRAMES_TARGET && cameraState === CAMERA_STATES.STABLE_OK) {
          setCameraState(CAMERA_STATES.CAPTURING);
          triggerAutoCapture();
        } else if (newCount >= Math.ceil(OK_FRAMES_TARGET * 0.7)) {
          setCameraState(CAMERA_STATES.STABLE_OK);
          setMessagesLiveness(getMessageFromConfig('OK'));
          setSubtitle(getMessageFromConfig('ALMOST_THERE'));
        } else {
          setCameraState(CAMERA_STATES.STABLE_OK);
          setMessagesLiveness(getMessageFromConfig('OK'));
          setSubtitle('');
        }
      } else {
        setOkFrameCount(0);
        setCameraState(CAMERA_STATES.READY);
        setSubtitle('');
      }
    },
    [okFrameCount, cameraState, getMessageFromConfig, triggerAutoCapture]
  );

  const formatMessageError = useCallback(
    (codes) => {
      const detectionResult = deriveStatus(codes);
      setDetectionStatus(detectionResult);
      const message = getMessageFromConfig(detectionResult);
      setMessagesLiveness(message);
      setMessage(message);
      updateProgress(codes);
    },
    [deriveStatus, getMessageFromConfig, updateProgress]
  );

  const triggerVibration = useCallback((pattern = [100]) => {
    if (navigator.vibrate) {
      try {
        navigator.vibrate(pattern);
      } catch (_) {}
    }
  }, []);

  const showPassScreen = useCallback(() => {
    setCameraState(CAMERA_STATES.PASS);
    setMessagesLiveness(getMessageFromConfig('SUCCESS'));
    triggerVibration([200, 100, 200]);
    setTimeout(() => {
      setCameraState(CAMERA_STATES.AUTO_CLOSE);
    }, AUTO_CLOSE_DELAY_MS);
  }, [getMessageFromConfig, triggerVibration]);

  const ensureCaptureDirectoryHandle = useCallback(async () => {
    if (captureDirectoryHandleRef.current) return captureDirectoryHandleRef.current;
    const picker = window.showDirectoryPicker;
    if (typeof picker !== 'function') {
      logEvent('directoryPicker.unavailable');
      return null;
    }
    try {
      const handle = await picker();
      captureDirectoryHandleRef.current = handle;
      const name = handle.name;
      logEvent('directoryPicker.selected', name ? { name } : undefined);
      return handle;
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') {
        logEvent('directoryPicker.cancelled');
      } else {
        logError('directoryPicker.error', error);
      }
      return null;
    }
  }, [logError, logEvent]);

  const saveCaptureArtifacts = useCallback(
    async (encryptedFile, result, videoBase64, videoMimeType) => {
      if (artifactsSavedRef.current) {
        logEvent('artifacts.skip', 'already saved for this session');
        return true;
      }
      const navigatorInfo = {
        userAgent: navigator.userAgent,
        language: navigator.language,
        languages: navigator.languages,
        platform: navigator.platform,
        vendor: navigator.vendor,
        hardwareConcurrency: navigator.hardwareConcurrency,
        deviceMemory: navigator.deviceMemory,
        maxTouchPoints: navigator.maxTouchPoints,
        userAgentData: navigator.userAgentData,
      };
      let mediaDevicesSummary = [];
      if (navigator.mediaDevices?.enumerateDevices) {
        try {
          const devices = await navigator.mediaDevices.enumerateDevices();
          mediaDevicesSummary = devices.map((device) => ({
            kind: device.kind,
            label: device.label,
            deviceId: device.deviceId,
            groupId: device.groupId,
          }));
        } catch (error) {
          logError('metadata.enumerateDevices.error', error);
        }
      }
      const windowInfo = {
        location: window.location.href,
        innerWidth: window.innerWidth,
        innerHeight: window.innerHeight,
        devicePixelRatio: window.devicePixelRatio,
        screenWidth: window.screen?.width,
        screenHeight: window.screen?.height,
        isSecureContext,
      };
      const metadata = {
        processId,
        savedAt: new Date().toISOString(),
        status: detectionStatus,
        statusMessage: getMessageFromConfig(detectionStatus),
        okFramesThreshold: OK_FRAMES_TARGET,
        okFramesAchieved: okFrameCountRef.current,
        detectionCodes: [...lastDetectionCodesRef.current],
        videoInfo: lastVideoInfoRef.current,
        navigator: navigatorInfo,
        mediaDevices: mediaDevicesSummary,
        window: windowInfo,
        bundleSizeBytes: encryptedFile.size,
        bundleMimeType: encryptedFile.type,
        livenessVideo: videoBase64
          ? {
              name: 'Liveness Video',
              mimeType: videoMimeType,
              sizeBytes: livenessVideoBlobRef.current?.size ?? null,
              recordedAt: recordingStartedAtRef.current,
            }
          : null,
        result,
        projectInfo: {
          projectName,
          codeTransaction,
          urlServiceLiveness,
        },
      };
      const metadataText = [
        '# Metadata',
        JSON.stringify(metadata, null, 2),
        '',
        ...(videoBase64 ? ['# Liveness Video (Base64)', `data:${videoMimeType};base64,${videoBase64}`, ''] : []),
        '# Event Log',
        ...eventLogRef.current,
        '',
      ].join('\n');
      const videoBase64Payload = videoBase64 ? `data:${videoMimeType};base64,${videoBase64}` : null;
      try {
        const directoryHandle = await ensureCaptureDirectoryHandle();
        if (directoryHandle) {
          const targetDirectory =
            'getDirectoryHandle' in directoryHandle ? await directoryHandle.getDirectoryHandle(processId, { create: true }) : directoryHandle;
          const directory = targetDirectory;
          await writeFileToDirectory(directory, 'bundle.bin', encryptedFile);
          await writeFileToDirectory(directory, 'metadata.txt', metadataText);
          if (videoBase64Payload) {
            await writeFileToDirectory(directory, 'LivenessVideoBase64.txt', videoBase64Payload);
          }
          if (livenessVideoBlobRef.current) {
            await writeFileToDirectory(directory, 'LivenessVideo.webm', livenessVideoBlobRef.current);
          }
          logEvent('artifacts.saved', { strategy: 'directory', processId });
          artifactsSavedRef.current = true;
          return true;
        }
      } catch (error) {
        logError('artifacts.directory.error', error);
      }
      try {
        logEvent('artifacts.saved', { strategy: 'no-download', processId });
        artifactsSavedRef.current = true;
        return true;
      } catch (error) {
        logError('artifacts.download.error', error);
        return false;
      }
    },
    [ensureCaptureDirectoryHandle, getMessageFromConfig, logError, logEvent, processId, projectName, codeTransaction, urlServiceLiveness, detectionStatus]
  );

  const finalizeLivenessVideo = useCallback(async () => {
    if (livenessVideoBase64Ref.current) return livenessVideoBase64Ref.current;
    if (typeof MediaRecorder === 'undefined') {
      logEvent('livenessVideo.finalize.unavailable');
      return null;
    }
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      if (!recordingStopPromiseRef.current) {
        recordingStopPromiseRef.current = new Promise((resolve) => {
          recordingStopResolverRef.current = resolve;
        });
      }
      try {
        mediaRecorderRef.current.stop();
      } catch (error) {
        logError('livenessVideo.stop.error', error);
        recordingStopResolverRef.current?.(null);
        recordingStopPromiseRef.current = null;
        return null;
      }
      return recordingStopPromiseRef.current;
    }
    return recordingStopPromiseRef.current ?? livenessVideoBase64Ref.current;
  }, [logError, logEvent]);

  const waitForBackgroundResult = useCallback(async () => {
    return new Promise((resolve) => {
      if (backgroundResultReadyRef.current && validationResultRef.current) {
        resolve(validationResultRef.current);
        return;
      }

      const checkInterval = setInterval(() => {
        if (backgroundResultReadyRef.current && validationResultRef.current) {
          clearInterval(checkInterval);
          resolve(validationResultRef.current);
        }
      }, 500);

      setTimeout(() => {
        clearInterval(checkInterval);
        if (!backgroundResultReadyRef.current) {
          logError('result.timeout', 'Result not available after 60s');
          resolve(null);
        }
      }, 60000);
    });
  }, [logError]);

  const handleWebSocketError = useCallback((error) => {
    logError('ws.process.error', error);

    setCameraState(CAMERA_STATES.FAIL);
    setMessagesLiveness(getMessageFromConfig('GENERAL_ERROR'));

    responseToHostError({
      error: error.errorType + ' aa' || 'WebSocket error ee',
      message: error.message || 'Unknown error',
      processKey
    });

    onReportEvent(
      EVENT_TRACER.LIVENESS.LivenessResponseToHost,
      'WebSocket error: ' + error.message
    );
  }, [logError, getMessageFromConfig, processKey, onReportEvent]);


    function createResultToHost(result, success) {

        const formattedResult = {
            success,
            keyBucketImage: '',
            keyBucketVideo: '',
            data: {
                score: projectName.toLowerCase().includes("billetera") ? 0.6 : 0.1,
                quality: result.quality,
                isAlive: result.isAlive,
                isFraud: result.isFraud,
                probability: result.probability,
                threshold_probabillity: '',
                threshold_quality: '',
            }
        }
        console.log("formattedResult", formattedResult)
        return {
            ...formattedResult,
            videoBase64: '',
            photoBase64: result.image,
            videoBlob: {}
        };
    }

  const displayFinalResult = useCallback((result) => {
      console.log('[SDK] fer displayFinalResult:', result);
    if (finalResultCapturedRef.current) {
      return;
    }
    console.log('[SDK] fer 2 displayFinalResult:', result);
    finalResultCapturedRef.current = true;

    logEvent('result.final', {
      decision: result.decision,
      isAlive: result.isAlive,
      isFraud: result.isFraud,
      probability: result.probability,
      hasImage: !!result.image
    });

    setFinalResult({
      event: result.event || 'COMPLETE',
      decision: result.decision,
      isAlive: result.isAlive,
      isFraud: result.isFraud,
      probability: result.probability,
      quality: result.quality,
      processId: result.processId,
      image: result.image,
      livenessProbability: result.livenessProbability,
      intentos: result.intentos,
      processKey: result.processKey || processKey
    });

    const isSuccess = result.isAlive && !result.isFraud;

    if (isSuccess) {
      // ✅ Mostrar pantalla de éxito y mantenerla visible
      setCameraState(CAMERA_STATES.PASS);
      setMessagesLiveness(getMessageFromConfig('SUCCESS'));
      triggerVibration([200, 100, 200]);

      const finalResponse = {
        success: true,
        platform,
        photo: result.image,
        isAlive: result.isAlive,
        isFraud: result.isFraud,
        decision: result.decision,
        probability: result.probability,
        quality: result.quality,
        livenessProbability: result.livenessProbability,
        processId: result.processId,
        processKey: result.processKey || processKey,
        intentos: result.intentos,
      };
        console.log('[SDK] fer Final response:', finalResponse);
      // Enviar respuesta al host pero mantener pantalla visible
      responseToHost(createResultToHost(result, true));

      if (wsClientRef.current) {
        wsClientRef.current.close();
      }

      // ❌ NO cerrar automáticamente - mantener resultado visible
      // Usuario debe cerrar manualmente o navegar

    } else {
      // ❌ Mostrar pantalla de fallo y mantenerla visible
      setCameraState(CAMERA_STATES.FAIL);
      setMessagesLiveness(getMessageFromConfig('LIVENESS_FAIL'));

        responseToHost(createResultToHost(result, false));

      if (wsClientRef.current) {
        wsClientRef.current.close();
      }

    }
  }, [
    logEvent,
    platform,
    processKey,
    responseToHost,
    responseToHostError,
    getMessageFromConfig,
    triggerVibration
  ]);

  const {
    clientRef: wsClientRef,
    initializeWebSocket
  } = useWebSocketLiveness({
    processKey,
    urlServiceLiveness,
    logEvent,
    logError,
    onReportEvent,
    onBundleValidated: useCallback((data) => {
      console.log('[SDK] Bundle validado - deteniendo grabación');

      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        mediaRecorderRef.current.stop();

        if (mediaStreamRef.current) {
          const tracks = mediaStreamRef.current.getTracks();
          tracks.forEach(track => {
            console.log('[SDK] Deteniendo track:', track.kind);
            track.stop();
          });
        }
      } else {
        console.log('[SDK] ⚠️ MediaRecorder no está activo o no existe');
      }

      setCameraState(CAMERA_STATES.PROCESSING_RESPONSE);
      setMessagesLiveness('');
      setSubtitle('');

      if (data.shouldRecordVideo) {
        console.log('[SDK] Video será procesado y enviado');
      } else {
        console.log('[SDK] Video NO requerido, esperando resultado');
      }
    }, []),
    onVideoUploaded: useCallback((data) => {
      console.log('[SDK] Video procesado, esperando resultado COMPLETE');
    }, []),
    onComplete: displayFinalResult,
    onError: undefined,
    responseToHostError,
    EVENT_TRACER,
  });

  useEffect(() => {
    if (urlServiceLiveness && processKey) {
      console.log('[SDK] Inicializando WebSocket');
      initializeWebSocket().then(client => {
        if (!client) {
          console.error('[SDK] ❌ No se pudo inicializar WebSocket');
          setCameraState(CAMERA_STATES.FAIL);
          setMessagesLiveness(
            language === 'es'
              ? 'Error de conexión. Verifica tu internet y vuelve a intentar.'
              : 'Connection error. Check your internet and try again.'
          );
        }
      }).catch(error => {
        console.error('[SDK] ❌ Error crítico inicializando WebSocket:', error);
      });
    } else {
        responseToHostError({
            message: 'Parámetros faltantes [urlServiceLiveness, processKey]'
        });
      console.warn('[SDK] ⚠️ Parámetros faltantes para WebSocket:', { urlServiceLiveness, processKey });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlServiceLiveness, processKey]);

  const loadSDK = useCallback(async () => {
    try {
      // Suprimir warnings del SDK
      const originalWarn = console.warn;
      const originalError = console.error;
      console.warn = (...args) => {
        const message = args[0]?.toString() || '';
        if (
          message.includes('development build') ||
          message.includes('Platform') ||
          message.includes('backend was already registered') ||
          message.includes('kernel') ||
          message.includes('is already registered')
        ) {
          return;
        }
        originalWarn.apply(console, args);
      };
      console.error = (...args) => {
        const message = args[0]?.toString() || '';
        if (
          message.includes('development build') ||
          message.includes('Platform') ||
          message.includes('backend was already registered') ||
          message.includes('kernel') ||
          message.includes('is already registered')
        ) {
          return;
        }
        originalError.apply(console, args);
      };
      await import('idlive-face-capture-web-development');
      console.warn = originalWarn;
      console.error = originalError;
      onReportEvent(EVENT_TRACER.SERVICE.InitServiceLiveness, 'SDK loaded');
    } catch (error) {
      onReportEvent(EVENT_TRACER.LIVENESS.LivenessCameraError, 'Failed to load SDK');
      responseToHostError({
          message: 'Failed to load SDK'
      });
    }
  }, [onReportEvent]);

  useEffect(() => {
    loadSDK();
  }, [loadSDK]);

  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      * { margin: 0 !important; padding: 0 !important; box-sizing: border-box !important; }
      html, body { width: 100vw !important; height: 100vh !important; overflow: hidden !important; position: fixed !important; -webkit-overflow-scrolling: touch !important; }
      #root { width: 100vw !important; height: 100vh !important; }
      idlive-face-capture { position: absolute !important; top: 0 !important; left: 0 !important; right: 0 !important; bottom: 0 !important; width: 100vw !important; height: 100vh !important; object-fit: cover !important; }
      idlive-face-capture video { position: absolute !important; top: 50% !important; left: 50% !important; transform: translate(-50%, -50%) !important; min-width: 100vw !important; min-height: 100vh !important; width: auto !important; height: auto !important; object-fit: cover !important; }
      idlive-face-capture canvas { position: absolute !important; top: 0 !important; left: 0 !important; width: 100vw !important; height: 100vh !important; }
      @media (max-width: 768px) {
        idlive-face-capture { position: fixed !important; top: 0 !important; left: 0 !important; right: 0 !important; bottom: 0 !important; width: 100% !important; height: 100% !important; z-index: 1 !important; }
        idlive-face-capture video { position: absolute !important; top: 0 !important; left: 0 !important; width: 100% !important; height: 100% !important; object-fit: cover !important; z-index: 1 !important; }
        idlive-face-capture canvas { position: absolute !important; top: 0 !important; left: 0 !important; width: 100% !important; height: 100% !important; z-index: 2 !important; }
      }
    `;
    document.head.appendChild(style);

    const element = wcRef.current;
    if (!element) return;


    if (sdkConfig.mask_hidden !== false) {
      //element.setAttribute('mask_hidden', '');
    }
    if (sdkConfig.auto_close_disabled !== false) {
      element.setAttribute('auto_close_disabled', '');
    }
    if (sdkConfig.auto_capture_disabled !== false) {
      element.setAttribute('auto_capture_disabled', '');
    }

    element.setAttribute('capture_mode', sdkConfig.capture_mode || 'encryptedPayload');
    element.setAttribute('payload_size', sdkConfig.payload_size || 'normal');
    element.setAttribute('device_id', sdkConfig.device_id || '');

    if (sdkConfig.oval_width) {
      element.setAttribute('oval_width', sdkConfig.oval_width);
    }
    if (sdkConfig.oval_height) {
      element.setAttribute('oval_height', sdkConfig.oval_height);
    }
    if (sdkConfig.oval_color) {
      element.setAttribute('oval_color', sdkConfig.oval_color);
    }
    if (sdkConfig.oval_stroke_width) {
      element.setAttribute('oval_stroke_width', sdkConfig.oval_stroke_width);
    }

    element.setAttribute('background_color', 'rgba(0, 0, 0, 0)');

    if (cameraConfig) {
      if (cameraConfig.width) {
        element.setAttribute('camera_width', JSON.stringify(cameraConfig.width));
      }
      if (cameraConfig.height) {
        element.setAttribute('camera_height', JSON.stringify(cameraConfig.height));
      }
      if (cameraConfig.frameRate) {
        element.setAttribute('camera_frame_rate', JSON.stringify(cameraConfig.frameRate));
      }
      if (cameraConfig.facingMode) {
        element.setAttribute('camera_facing_mode', cameraConfig.facingMode);
      }
    }

    element.setAttribute('sunglasses_detected_check_enabled', '');
    element.setAttribute('face_not_centered_check_enabled', '');
    element.setAttribute('dark_image_check_enabled', '');
    element.setAttribute('face_blurry_check_enabled', '');
    element.setAttribute('eyes_closed_check_enabled', '');
    element.setAttribute('mouth_open_check_enabled', '');

    const eventTarget = element;

    const handleBeforeInitialize = () => {
      // Log reducido
      // console.log('[SDK] beforeInitialize');
    };

    const handleInitialize = () => {
      // Solo log crítico
      console.log('[SDK] ✅ Inicializado');
      onReportEvent(EVENT_TRACER.SERVICE.InitServiceLiveness, 'SDK initialized');
      ensureCameraReady();
      artifactsSavedRef.current = false;
      eventLogRef.current = [];
      livenessVideoBase64Ref.current = null;
      livenessVideoBlobRef.current = null;
      recordedChunksRef.current = [];
      recordingStartedAtRef.current = new Date().toISOString();

      const externalMeta = {
        processId,
        projectName: projectName || 'Default Project',
        codeTransaction: codeTransaction || 'default-transaction',
        sessionStartedAt: sessionStartedAtRef.current,
        environment: process.env.NODE_ENV || 'development',
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        language: navigator.language,
        languages: navigator.languages,
        userAgent: navigator.userAgent,
        platform: navigator.platform,
        devicePixelRatio: window.devicePixelRatio,
        viewport: { width: window.innerWidth, height: window.innerHeight },
        screen: { width: window.screen?.width, height: window.screen?.height },
        maxTouchPoints: navigator.maxTouchPoints,
      };
      try {
        element.setExternalMeta?.(JSON.stringify(externalMeta));
        logEvent('setExternalMeta', externalMeta);
      } catch (error) {
        logError('setExternalMeta.error', error);
      }

      if (!hasOpenedCameraRef.current) {
        hasOpenedCameraRef.current = true;
        try {
          element.openCamera?.();
          logEvent('openCamera.requested');
          onReportEvent(EVENT_TRACER.LIVENESS.LivenessCameraOpen, 'Camera open requested');
        } catch (error) {
          console.error('[SDK] ❌ Error abriendo cámara:', error);
          onReportEvent(EVENT_TRACER.LIVENESS.LivenessCameraError, 'Failed to open camera');
        }
      }
    };

    const handleBeforeOpen = () => {
      // Log reducido
      // console.log('[SDK] beforeOpen');
    };

    const handleOpen = (event) => {
      const detail = event.detail?.[0];
      const sourceVideo = detail?.video;

      // Log reducido - solo crítico
      console.log('[SDK] 📹 Cámara abierta');
      setLoading(false)

      if (sourceVideo && customVideoRef.current) {
        customVideoRef.current.srcObject = sourceVideo.srcObject;
      }

      if (sourceVideo) {
        const mediaStream = sourceVideo.srcObject;
        mediaStreamRef.current = mediaStream;
          streamRef.current = mediaStream;
        const track = mediaStream?.getVideoTracks?.()?.[0];
        const settings = track?.getSettings ? track.getSettings() : undefined;
        lastVideoInfoRef.current = {
          videoWidth: sourceVideo.videoWidth,
          videoHeight: sourceVideo.videoHeight,
          readyState: sourceVideo.readyState,
          trackLabel: track?.label,
          deviceId: settings?.deviceId,
          facingMode: settings?.facingMode,
          frameRate: settings?.frameRate,
          aspectRatio: settings?.aspectRatio,
          groupId: settings?.groupId,
          heightSetting: settings?.height,
          widthSetting: settings?.width,
        };

        // NO iniciar MediaRecorder aquí - solo se usa el parallelRecording que inicia en beforeCapture
        // El video que se envía al backend es el de parallelRecording, no este
      }

      if (cameraState === CAMERA_STATES.CHECKING_CAMERA) {
        setCameraState(CAMERA_STATES.READY);
        setMessagesLiveness(getMessageFromConfig('INIT_TEXT'));
        setIsLibraryVisible(true);
      }
    };

    const handleDetection = (event, type) => {
      const raw = event.detail?.[0];
      const errors = raw?.errors || raw || [];
      const codes = normalizeDetectionPayload(raw);
      lastDetectionCodesRef.current = [...codes];
      // Logs del SDK reducidos - solo para debugging
      // if (type === 'faceDetection') {
      //   console.log('[SDK] faceDetection:', codes);
      // } else if (type === 'detection') {
      //   console.log('[SDK] detection:', codes);
      // }

      if (cameraState === CAMERA_STATES.READY || cameraState === CAMERA_STATES.STABLE_OK) {
        formatMessageError(codes);
          if (errors.length === 0) {
              // setMessage('¡Perfecto! 👍');
              setBorderColor(configStyles?.successColor || '#10B981');
          } else {
              setBorderColor(configStyles?.errorColor || '#EF4444');
              if (errors.includes('FACE_NOT_CENTERED')) {
                  setMessage('Centra tu rostro');
              }
          }
      }
    };

    const handleFaceDetection = (event) => handleDetection(event, 'faceDetection');
    const handleQualityDetection = (event) => handleDetection(event, 'detection');

    const handleBeforeCapture = async () => {
      console.log('[SDK] 📸 Iniciando captura');

      setIsCapturing(true);
      setCameraState(CAMERA_STATES.CAPTURING);
      setMessagesLiveness(getMessageFromConfig('END_TEXT'));
      setSubtitle('');
      triggerVibration([100]);
      onReportEvent(EVENT_TRACER.LIVENESS.LivenessCaptureSuccess, 'Capture started');

      try {
        const cameraStream = mediaStreamRef.current;

        if (cameraStream && cameraStream.active) {
          if (wsClientRef.current && wsClientRef.current.isSocketConnected()) {
            console.log('[SDK] 🎬 INICIANDO GRABACIÓN DE VIDEO (duración mínima: 5 segundos)');
            await wsClientRef.current.startParallelRecording(cameraStream);
            console.log('[SDK] ✅ Grabación de video iniciada correctamente');
            setRecordingVideo(true)
            setLoading(true);
            onReportEvent(EVENT_TRACER.LIVENESS.LivenessInitCaptureVideo, 'Video recording started - 5 sec minimum');
          } else {
            console.warn('[SDK] ⚠️ WebSocket no conectado, no se puede grabar video');
              responseToHostError({
                  message: 'WebSocket no conectado'
              });
          }
        } else {
          console.warn('[SDK] ⚠️ No hay stream de cámara activo');
        }
      } catch (error) {
        console.error('[SDK]  Error al iniciar grabación de video:', error);
        // No bloquear la captura si falla la grabación
      }
    };

    const handleCapture = async (event) => {
      const detail = event.detail?.[0];

      // Log reducido del capture
      console.log('[SDK] 📸 Captura completada:', {
        photoSize: detail?.photo?.size,
        bundleSize: detail?.encryptedFile?.size
      });

      autoCapturePendingRef.current = false;

      if (!detail?.encryptedFile) {
        logError('capture.missingEncryptedFile');
        setIsCapturing(false);
        isCapturingRef.current = false;
        return;
      }

      // IMPORTANTE: NO cerrar la cámara todavía
      // La cámara se cerrará después de detener la grabación de video
      // para evitar que el MediaRecorder se detenga prematuramente

      setCameraState(CAMERA_STATES.VALIDATING_BUNDLE);
      setMessagesLiveness(
        language === 'es'
          ? 'Validando captura...'
          : 'Validating capture...'
      );
      setSubtitle('');

      try {
        if (!wsClientRef.current || !wsClientRef.current.isSocketConnected()) {
          throw new Error('WebSocket not connected');
        }

        // IMPORTANTE: Ya NO necesitamos el cameraStream aquí porque la grabación
        // ya se inició en beforeCapture. Solo necesitamos detenerla y obtener el video.

        logEvent('capture.sending.start', {
          bundleSize: detail.encryptedFile.size
        });

        const deviceMetadata = {
          processId: processKey,
          projectName,
          codeTransaction,
          timestamp: new Date().toISOString(),
          device: {
            userAgent: navigator.userAgent,
            platform: navigator.platform,
            vendor: navigator.vendor,
            language: navigator.language,
            languages: Array.from(navigator.languages || []),
            hardwareConcurrency: navigator.hardwareConcurrency,
            deviceMemory: navigator.deviceMemory,
            maxTouchPoints: navigator.maxTouchPoints,
            connection: {
              effectiveType: navigator.connection?.effectiveType,
              downlink: navigator.connection?.downlink,
              rtt: navigator.connection?.rtt,
              saveData: navigator.connection?.saveData,
            },
            userAgentData: navigator.userAgentData
              ? {
                  brands: navigator.userAgentData.brands,
                  mobile: navigator.userAgentData.mobile,
                  platform: navigator.userAgentData.platform
                }
              : null,
          },
          screen: {
            width: window.screen?.width,
            height: window.screen?.height,
            availWidth: window.screen?.availWidth,
            availHeight: window.screen?.availHeight,
            colorDepth: window.screen?.colorDepth,
            pixelDepth: window.screen?.pixelDepth,
            orientation: window.screen?.orientation?.type,
            angle: window.screen?.orientation?.angle,
          },
          window: {
            innerWidth: window.innerWidth,
            innerHeight: window.innerHeight,
            outerWidth: window.outerWidth,
            outerHeight: window.outerHeight,
            devicePixelRatio: window.devicePixelRatio,
            location: {
              href: window.location.href,
              protocol: window.location.protocol,
              host: window.location.host,
              hostname: window.location.hostname,
              port: window.location.port,
              pathname: window.location.pathname,
              search: window.location.search,
              hash: window.location.hash,
            },
            isSecureContext: window.isSecureContext,
          },
          camera: {
            devices: [],
            activeDevice: null,
            constraints: null,
            settings: null,
            capabilities: null
          },
          performance: {
            memory: performance.memory
              ? {
                  jsHeapSizeLimit: performance.memory.jsHeapSizeLimit,
                  totalJSHeapSize: performance.memory.totalJSHeapSize,
                  usedJSHeapSize: performance.memory.usedJSHeapSize,
                }
              : null,
            navigation: {
              type: performance.navigation?.type,
              redirectCount: performance.navigation?.redirectCount
            },
            timing: performance.timing
              ? {
                  loadTime: performance.timing.loadEventEnd - performance.timing.navigationStart,
                  domReady: performance.timing.domContentLoadedEventEnd - performance.timing.navigationStart,
                }
              : null,
          },
        };

        try {
          const devices = await navigator.mediaDevices.enumerateDevices();
          deviceMetadata.camera.devices = devices.map((d) => ({
            deviceId: d.deviceId,
            kind: d.kind,
            label: d.label,
            groupId: d.groupId,
          }));

          if (mediaStreamRef.current) {
            const videoTrack = mediaStreamRef.current.getVideoTracks()[0];
            if (videoTrack) {
              deviceMetadata.camera.activeDevice = {
                id: videoTrack.id,
                label: videoTrack.label,
                enabled: videoTrack.enabled,
                muted: videoTrack.muted,
                readyState: videoTrack.readyState,
              };
              deviceMetadata.camera.settings = videoTrack.getSettings ? videoTrack.getSettings() : null;
              deviceMetadata.camera.capabilities = videoTrack.getCapabilities ? videoTrack.getCapabilities() : null;
              deviceMetadata.camera.constraints = videoTrack.getConstraints ? videoTrack.getConstraints() : null;
            }
          }
        } catch (error) {
          logError('metadata.camera.error', error);
        }

        try {
          if (navigator.getBattery) {
            const battery = await navigator.getBattery();
            deviceMetadata.battery = {
              charging: battery.charging,
              level: battery.level,
              chargingTime: battery.chargingTime,
              dischargingTime: battery.dischargingTime,
            };
          }
        } catch (error) {
          logError('metadata.battery.error', error);
        }

        try {
          if (navigator.permissions) {
            const cameraPermission = await navigator.permissions.query({ name: 'camera' });
            deviceMetadata.permissions = { camera: cameraPermission.state };
          }
        } catch (error) {
          logError('metadata.permissions.error', error);
        }

        // NUEVO FLUJO OPTIMIZADO:
        // La grabación de video YA se inició en beforeCapture (cuando el stream estaba activo)
        // Ahora necesitamos:
        // 1. Detener la grabación y obtener el video
        // 2. Enviar bundle
        // 3. Enviar video
        // 4. FINALMENTE cerrar la cámara
        console.log('[V7] Deteniendo grabación y enviando datos...');

        await wsClientRef.current.stopRecordingAndSend(
          detail.encryptedFile,
          deviceMetadata
        );

        console.log('[V7] Bundle y video enviados exitosamente');

        // IMPORTANTE: Cerrar la cámara DESPUÉS de enviar todo
        const element = wcRef.current;
        if (element?.closeCamera) {
          console.log('[SDK] 📷 Cerrando cámara después de envío completo');
          // element.closeCamera();
        }

        logEvent('capture.complete', {
          bundleSent: true,
          videoSent: true
        });



        onReportEvent(
          EVENT_TRACER.LIVENESS.LivenessCaptureSuccess,
          'Capture and upload completed'
        );

      } catch (error) {
        logError('capture.parallel.error', error);

        setCameraState(CAMERA_STATES.FAIL);
        setMessagesLiveness(getMessageFromConfig('GENERAL_ERROR'));

        responseToHostError({
          error: 'Capture processing error',
          message: error.message | 'unknown error',
          processKey
        });

        onReportEvent(
          EVENT_TRACER.LIVENESS.LivenessResponseToHost,
          'Capture error: ' + error.message
        );

        setIsCapturing(false);
        isCapturingRef.current = false;
      }
    };

    const handleFraudShieldFailure = (captureProbability, faceProbability) => {
      setCameraState(CAMERA_STATES.FAIL);
      setMessagesLiveness(getMessageFromConfig('LIVENESS_FAIL'));
      setOkFrameCount(0);
      responseToHostError({
        error: 'Liveness validation failed',
        message: 'Liveness validation failed',
        captureProbability,
        faceProbability,
        processKey,
      });
      setIsCapturing(false);
    };

    const handleFraudShieldError = (error) => {
      onReportEvent(EVENT_TRACER.LIVENESS.LivenessResponseToHost, 'FraudShield service error');
      setCameraState(CAMERA_STATES.FAIL);
      setMessagesLiveness(getMessageFromConfig('GENERAL_ERROR'));
      responseToHostError({
        error: 'Service error during liveness verification',
        message: error.message,
      });
      setIsCapturing(false);
    };

    const handleClose = async () => {
      // Log reducido
      console.log('[SDK] 📷 Cámara cerrada');

      setOkFrameCount(0);
      okFrameCountRef.current = 0;
      setIsCapturing(false);
      isCapturingRef.current = false;
      autoCapturePendingRef.current = false;

      // Detener MediaRecorder si está activo
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        try {
          mediaRecorderRef.current.stop();
        } catch (error) {
          console.error('[SDK] ❌ Error deteniendo MediaRecorder:', error);
        }
      }

      // NO cerrar WebSocket aquí - se cierra automáticamente después de COMPLETE
      console.log('[SDK] WebSocket permanece activo para recibir resultados');

      try {
          responseToHostError({
              message: 'Camera closed by user or system'
          });
      } catch (error) {
        logError('process.close.response.error', error);
          responseToHostError({
              message: 'Camera closed by user or system'
          });
      }
    };

    const handleError = (event) => {
      const detail = event.detail?.[0];

      console.log('%c[SDK EVENT] error', 'color: #EF4444; font-weight: bold;');
      console.log('Description: A critical error occurred');
      console.log('Returns: String with the text of the error');
      console.log('Error Message:', detail?.message);
      console.log('Error Detail:', detail);
      console.log('---');

      logError('event.error', detail ?? event);
      const message = detail?.message?.toLowerCase() ?? '';
      if (message.includes('denied') || message.includes('not allowed')) {
        setCameraState(CAMERA_STATES.PERMISSION_DENIED);
        setMessagesLiveness(getMessageFromConfig('PERMISSION_DENIED'));
        onReportEvent(EVENT_TRACER.LIVENESS.LivenessCameraError, 'Camera permission denied');
        responseToHostError({
            message: 'Camera permission denied'
        });
      } else {
        setCameraState(CAMERA_STATES.PERMISSION_DENIED);
        setMessagesLiveness(getMessageFromConfig('GENERAL_ERROR'));
        onReportEvent(EVENT_TRACER.LIVENESS.LivenessCameraError, 'General camera error');
        responseToHostError({
            message: 'General camera error'
        });
      }
      setOkFrameCount(0);
      setIsCapturing(false);
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        try {
          mediaRecorderRef.current.stop();
        } catch (error) {
          logError('livenessVideo.stopOnError.error', error);
        }
      }
    };

    const handleLicenseInfo = (event) => {
      const detail = event.detail?.[0];

      console.log('%c[SDK EVENT] licenseInfo', 'color: #6366F1; font-weight: bold;');
      console.log('Description: Event that occurs after requesting license information using getLicenseInfo action');
      console.log('Returns: Expiration date');
      console.log('License Info:', detail);
      console.log('---');

      logEvent('event.licenseInfo', detail);
    };

    // Log inicial - comentado para reducir ruido
    // console.log('[SDK] Event Listeners configurados');

    eventTarget.addEventListener('beforeInitialize', handleBeforeInitialize);
    eventTarget.addEventListener('initialize', handleInitialize);
    eventTarget.addEventListener('beforeOpen', handleBeforeOpen);
    eventTarget.addEventListener('open', handleOpen);
    eventTarget.addEventListener('faceDetection', handleFaceDetection);
    eventTarget.addEventListener('detection', handleQualityDetection);
    eventTarget.addEventListener('beforeCapture', handleBeforeCapture);
    eventTarget.addEventListener('capture', handleCapture);
    eventTarget.addEventListener('close', handleClose);
    eventTarget.addEventListener('error', handleError);
    eventTarget.addEventListener('licenseInfo', handleLicenseInfo);

    return () => {
      if (closingTimeoutRef.current) clearTimeout(closingTimeoutRef.current);
      closingTimeoutRef.current = null;
      hasOpenedCameraRef.current = false;
      eventTarget.removeEventListener('beforeInitialize', handleBeforeInitialize);
      eventTarget.removeEventListener('initialize', handleInitialize);
      eventTarget.removeEventListener('beforeOpen', handleBeforeOpen);
      eventTarget.removeEventListener('open', handleOpen);
      eventTarget.removeEventListener('faceDetection', handleFaceDetection);
      eventTarget.removeEventListener('detection', handleQualityDetection);
      eventTarget.removeEventListener('beforeCapture', handleBeforeCapture);
      eventTarget.removeEventListener('capture', handleCapture);
      eventTarget.removeEventListener('close', handleClose);
      eventTarget.removeEventListener('error', handleError);
      eventTarget.removeEventListener('licenseInfo', handleLicenseInfo);
      document.head.removeChild(style);
    };
  }, [
    logError,
    logEvent,
    processId,
    saveCaptureArtifacts,
    triggerAutoCapture,
    onReportEvent,
    responseToHost,
    responseToHostError,
    finalizeLivenessVideo,
    formatMessageError,
    cameraState,
    codeTransaction,
    ensureCameraReady,
    getMessageFromConfig,
    projectName,
    showPassScreen,
    triggerVibration,
    language,
    urlServiceLiveness,
    processKey,
    waitForBackgroundResult,
    displayFinalResult,
    platform,
  ]);

  useEffect(() => {
    const handleBeforeUnload = () => {
      if (
        cameraState !== CAMERA_STATES.PASS &&
        cameraState !== CAMERA_STATES.AUTO_CLOSE &&
        cameraState !== CAMERA_STATES.CANCELLED
      ) {
        responseToHost({
          success: false,
          cancelled: true,
          reason: 'User closed or refreshed page',
          processId,
          processKey,
        });
        logEvent('process.cancelled.beforeunload', { processId, processKey });
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [cameraState, logEvent, processId, processKey, responseToHost]);

  useEffect(() => {
    if (config?.messages && cameraState === CAMERA_STATES.CHECKING_CAMERA) {
      setMessagesLiveness(getMessageFromConfig('INIT_TEXT'));
    }
  }, [config, getMessageFromConfig, cameraState]);

  useEffect(() => {
    return () => {
      if (mediaStreamRef.current) {
        mediaStreamRef.current.getTracks().forEach((track) => track.stop());
      }
    };
  }, []);

  const renderLoader = () => (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '100%',
      height: '100%',
      transform: 'scaleX(-1)',
    }}>
      {isLuloProject ? <LoaderLulo type="v2" /> : <LoaderIOS />}
    </div>
  );

  const renderCheckingCameraScreen = () => (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100vw',
      height: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 10,
      backgroundColor: configStyles.loaderOverlay?.backgroundColor || 'rgba(0, 0, 0, 0.8)',
    }}>
      {renderLoader()}
    </div>
  );

  const renderVideoUploadScreen = () => (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100vw',
      height: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 10,
      backgroundColor: configStyles.loaderOverlay?.backgroundColor || 'rgba(0, 0, 0, 0.8)',
    }}>
      {renderLoader()}
    </div>
  );

  const renderProcessingResponseScreen = () => (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100vw',
      height: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 10,
      backgroundColor: configStyles.loaderOverlay?.backgroundColor || 'rgba(0, 0, 0, 0.8)',
    }}>
      {renderLoader()}
    </div>
  );

  const renderValidationCompleteScreen = () => (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100vw',
      height: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 10,
      backgroundColor: configStyles.loaderOverlay?.backgroundColor || 'rgba(0, 0, 0, 0.8)',
    }}>
      {renderLoader()}
    </div>
  );

  const renderPermissionDeniedScreen = () => (
    <div style={configStyles.loaderOverlay}>
      <div style={{ fontSize: '48px', color: '#EF4444', marginBottom: '24px', textAlign: 'center' }} />
      <h2 style={{ color: '#ffffff', fontSize: '20px', textAlign: 'center', margin: '0 0 16px 0', fontWeight: '600' }}>Acceso a la cámara requerido</h2>
      <p
        style={{
          color: '#ffffff',
          fontSize: '16px',
          textAlign: 'center',
          maxWidth: '320px',
          opacity: 0.8,
          lineHeight: 1.5,
          margin: 0,
        }}
      >
        {messagesLiveness}
      </p>
    </div>
  );

  const renderPassScreen = () => {
    const isValid = finalResult?.isAlive === true && finalResult?.isFraud === false;
    const statusColor = isValid ? '#10B981' : '#EF4444';
    const statusBg = isValid ? 'rgba(16, 185, 129, 0.15)' : 'rgba(239, 68, 68, 0.15)';
    const statusText = isValid ? 'VÁLIDO' : 'INVÁLIDO';

    return (
      <div style={configStyles.passOverlay}>
        {finalResult ? (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'flex-start',
              height: '100%',
              padding: '24px 20px',
              maxWidth: '100vw',
              overflowY: 'auto',
            }}
          >
            {/* Título */}
            <div style={{
              fontSize: '22px',
              fontWeight: '700',
              color: '#ffffff',
              marginBottom: '24px',
              textAlign: 'center',
              letterSpacing: '0.5px'
            }}>
              Resultado de Validación Liveness
            </div>

            {/* Imagen */}
            {finalResult.image && (
              <div style={{
                width: '140px',
                height: '140px',
                borderRadius: '50%',
                overflow: 'hidden',
                marginBottom: '24px',
                border: `4px solid ${statusColor}`,
                boxShadow: `0 0 20px ${statusColor}40, 0 4px 12px rgba(0,0,0,0.3)`
              }}>
                <img
                  src={`data:image/jpeg;base64,${finalResult.image}`}
                  alt="Resultado"
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              </div>
            )}

            {/* Estado Principal - CORREGIDO */}
            <div style={{
              backgroundColor: statusBg,
              borderRadius: '16px',
              padding: '20px 32px',
              width: '100%',
              maxWidth: '500px',
              marginBottom: '20px',
              border: `3px solid ${statusColor}`,
              boxShadow: `0 4px 16px ${statusColor}20`
            }}>
              <div style={{
                fontSize: '24px',
                fontWeight: '700',
                textAlign: 'center',
                color: statusColor,
                letterSpacing: '1px'
              }}>
                {statusText}
              </div>
            </div>

            {/* Panel de Detección */}
            <div style={{
              backgroundColor: 'rgba(255, 255, 255, 0.08)',
              borderRadius: '16px',
              padding: '24px',
              width: '100%',
              maxWidth: '500px',
              marginBottom: '20px',
              border: '1px solid rgba(255, 255, 255, 0.1)'
            }}>
              <div style={{
                fontSize: '16px',
                fontWeight: '600',
                color: '#ffffff',
                marginBottom: '16px',
                paddingBottom: '12px',
                borderBottom: '2px solid rgba(255, 255, 255, 0.1)'
              }}>
                Resultados de Detección
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
                <div style={{
                  backgroundColor: 'rgba(0,0,0,0.3)',
                  padding: '16px',
                  borderRadius: '12px',
                  border: `2px solid ${finalResult.isAlive ? '#10B981' : '#EF4444'}`,
                  transition: 'all 0.2s'
                }}>
                  <div style={{ color: '#9CA3AF', fontSize: '13px', marginBottom: '6px', fontWeight: '500' }}>Es Vivo</div>
                  <div style={{
                    color: finalResult.isAlive ? '#10B981' : '#EF4444',
                    fontWeight: '700',
                    fontSize: '18px'
                  }}>
                    {finalResult.isAlive ? 'Sí' : 'No'}
                  </div>
                </div>

                <div style={{
                  backgroundColor: 'rgba(0,0,0,0.3)',
                  padding: '16px',
                  borderRadius: '12px',
                  border: `2px solid ${finalResult.isFraud ? '#EF4444' : '#10B981'}`,
                  transition: 'all 0.2s'
                }}>
                  <div style={{ color: '#9CA3AF', fontSize: '13px', marginBottom: '6px', fontWeight: '500' }}>Es Fraude</div>
                  <div style={{
                    color: finalResult.isFraud ? '#EF4444' : '#10B981',
                    fontWeight: '700',
                    fontSize: '18px'
                  }}>
                    {finalResult.isFraud ? 'Sí' : 'No'}
                  </div>
                </div>

                <div style={{
                  backgroundColor: 'rgba(0,0,0,0.3)',
                  padding: '16px',
                  borderRadius: '12px',
                  border: '1px solid rgba(255, 255, 255, 0.1)'
                }}>
                  <div style={{ color: '#9CA3AF', fontSize: '13px', marginBottom: '6px', fontWeight: '500' }}>Probabilidad</div>
                  <div style={{ color: '#ffffff', fontWeight: '700', fontSize: '18px' }}>
                    {(finalResult.probability * 100).toFixed(2)}%
                  </div>
                </div>

                <div style={{
                  backgroundColor: 'rgba(0,0,0,0.3)',
                  padding: '16px',
                  borderRadius: '12px',
                  border: '1px solid rgba(255, 255, 255, 0.1)'
                }}>
                  <div style={{ color: '#9CA3AF', fontSize: '13px', marginBottom: '6px', fontWeight: '500' }}>Calidad</div>
                  <div style={{ color: '#ffffff', fontWeight: '700', fontSize: '18px' }}>
                    {((finalResult.quality || 0) * 100).toFixed(2)}%
                  </div>
                </div>
              </div>
            </div>

            {/* Panel de Información del Proceso */}
            <div style={{
              backgroundColor: 'rgba(255, 255, 255, 0.08)',
              borderRadius: '16px',
              padding: '24px',
              width: '100%',
              maxWidth: '500px',
              marginBottom: '20px',
              border: '1px solid rgba(255, 255, 255, 0.1)'
            }}>
              <div style={{
                fontSize: '16px',
                fontWeight: '600',
                color: '#ffffff',
                marginBottom: '16px',
                paddingBottom: '12px',
                borderBottom: '2px solid rgba(255, 255, 255, 0.1)'
              }}>
                Información del Proceso
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  padding: '12px',
                  backgroundColor: 'rgba(0,0,0,0.2)',
                  borderRadius: '8px'
                }}>
                  <span style={{ color: '#9CA3AF', fontSize: '14px', fontWeight: '500' }}>Evento</span>
                  <span style={{ color: '#10B981', fontWeight: '600', fontSize: '14px' }}>{finalResult.event}</span>
                </div>

                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  padding: '12px',
                  backgroundColor: 'rgba(0,0,0,0.2)',
                  borderRadius: '8px'
                }}>
                  <span style={{ color: '#9CA3AF', fontSize: '14px', fontWeight: '500' }}>Intentos</span>
                  <span style={{ color: '#ffffff', fontWeight: '600', fontSize: '14px' }}>{finalResult.intentos || 1}</span>
                </div>

                {finalResult.livenessProbability !== undefined && (
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    padding: '12px',
                    backgroundColor: 'rgba(0,0,0,0.2)',
                    borderRadius: '8px'
                  }}>
                    <span style={{ color: '#9CA3AF', fontSize: '14px', fontWeight: '500' }}>Prob. Liveness</span>
                    <span style={{ color: '#ffffff', fontWeight: '600', fontSize: '14px' }}>
                      {(finalResult.livenessProbability * 100).toFixed(2)}%
                    </span>
                  </div>
                )}

                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '6px',
                  padding: '12px',
                  backgroundColor: 'rgba(0,0,0,0.2)',
                  borderRadius: '8px'
                }}>
                  <span style={{ color: '#9CA3AF', fontSize: '13px', fontWeight: '500' }}>Process ID</span>
                  <span style={{
                    color: '#ffffff',
                    fontSize: '11px',
                    fontFamily: 'monospace',
                    backgroundColor: 'rgba(0,0,0,0.4)',
                    padding: '8px',
                    borderRadius: '6px',
                    wordBreak: 'break-all',
                    lineHeight: '1.4'
                  }}>
                    {finalResult.processId}
                  </span>
                </div>
              </div>
            </div>

            {/* JSON Completo (Collapsible) */}
            <details style={{
              backgroundColor: 'rgba(255, 255, 255, 0.05)',
              borderRadius: '16px',
              padding: '20px',
              width: '100%',
              maxWidth: '500px',
              cursor: 'pointer',
              border: '1px solid rgba(255, 255, 255, 0.1)'
            }}>
              <summary style={{
                color: '#F59E0B',
                fontWeight: '600',
                fontSize: '15px',
                userSelect: 'none',
                outline: 'none',
                cursor: 'pointer'
              }}>
                Ver Objeto JSON Completo
              </summary>
              <pre style={{
                backgroundColor: 'rgba(0, 0, 0, 0.6)',
                color: '#10B981',
                padding: '20px',
                borderRadius: '12px',
                fontSize: '12px',
                overflow: 'auto',
                marginTop: '16px',
                maxHeight: '350px',
                fontFamily: 'monospace',
                lineHeight: '1.6',
                border: '1px solid rgba(16, 185, 129, 0.2)'
              }}>
                {JSON.stringify(finalResult, null, 2)}
              </pre>
            </details>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
            <div
              style={{
                width: '80px',
                height: '80px',
                border: '4px solid rgba(16, 185, 129, 0.2)',
                borderTop: '4px solid #10B981',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
                marginBottom: '0',
              }}
            />
          </div>
        )}
      </div>
    );
  };

  const getOvalColor = useCallback(() => {
    const hasNoFaceError = lastDetectionCodesRef.current.includes('FACE_NOT_FOUND');
    if (hasNoFaceError || (cameraState === CAMERA_STATES.READY && lastDetectionCodesRef.current.length > 0)) return '#EF4444';
    if (cameraState === CAMERA_STATES.STABLE_OK || cameraState === CAMERA_STATES.CAPTURING) return '#10B981';
    return 'rgba(255, 255, 255, 0.8)';
  }, [cameraState]);


console.log('messagesLiveness', messagesLiveness);


  const renderDynamicOval = useCallback(() => {
    const ovalColor = getOvalColor();

    let ovalSize;
    if (window.innerWidth <= 600) {
      ovalSize = { width: '60vw', height: '35vh' }; // Más pequeño
    } else if (window.innerWidth <= 768) {
      ovalSize = { width: '60vw', height: '35vh' }; // Más pequeño
    } else if (window.innerWidth <= 992) {
      ovalSize = { width: '25vw', height: '40vh' }; // Más pequeño
    } else {
      ovalSize = { width: '30vw', height: '60vh' }; // Más pequeño
    }

    const shouldShow =
      isLibraryVisible &&
      (cameraState === CAMERA_STATES.READY ||
      cameraState === CAMERA_STATES.STABLE_OK ||
      cameraState === CAMERA_STATES.CAPTURING ||
      cameraState === CAMERA_STATES.FAIL);

    // Si no debe mostrarse, no renderizar nada
    if (!shouldShow) {
      return null;
    }

    return (
      <div
        style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          zIndex: 2,
          transition: 'opacity 0.3s ease',
          pointerEvents: 'none',
        }}
      >
        {messagesLiveness && (
          <div
            style={{
              position: 'absolute',
              left: '50%',
              transform: 'translateX(-50%) scaleX(-1)',
              top: '-8rem', // Más arriba
              width: '100%',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              flexDirection: 'column',
            }}
          >
            <h2
              style={{
                background: 'rgba(0, 0, 0, 0.5)',
                textAlign: 'center',
                fontSize: '13px',
                color: configStyles.headerText?.color || 'white',
                borderRadius: '6px',
                width: 'fit-content',
                margin: 'auto',
                whiteSpace: 'nowrap',
                padding: '8px 16px',
                transform: 'scaleX(-1)',
              }}
            >
              {messagesLiveness}
            </h2>
          </div>
        )}

        <div
          style={{
            border: `4px solid ${ovalColor}`,
            boxSizing: 'border-box',
            borderRadius: '50%',
            overflow: 'hidden',
            position: 'relative',
            width: ovalSize.width,
            height: ovalSize.height,
            zIndex: 2,
          }}
        >
          <video
            ref={customVideoRef}
            autoPlay
            playsInline
            muted
            onLoadedMetadata={(e) => {
              e.target.play().catch(err => console.error('Error playing video:', err));
            }}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              transform: 'scaleX(-1)',
              position: 'relative',
            }}
          />
        </div>
      </div>
    );
  }, [getOvalColor, messagesLiveness, configStyles.headerText, cameraState]);

  const renderLivenessUI = () => (
    <>
      {renderDynamicOval()}
    </>
  );

  const showLoader = (!emitReadyCam || emitReadyCam === 'false') ? true : (emitReadyCam && emitReadyCam === 'true') ? loadingMetaData : false;

  const renderCurrentState = () => {
    switch (cameraState) {
      case CAMERA_STATES.CHECKING_CAMERA:
        return renderCheckingCameraScreen();
      case CAMERA_STATES.VALIDATING_BUNDLE:
        return renderCheckingCameraScreen();
      case CAMERA_STATES.UPLOADING_VIDEO:
        return renderVideoUploadScreen();
      case CAMERA_STATES.PROCESSING_RESPONSE:
        return renderProcessingResponseScreen();
      case CAMERA_STATES.VALIDATION_COMPLETE:
        return renderValidationCompleteScreen();
      case CAMERA_STATES.PERMISSION_DENIED:
        return renderPermissionDeniedScreen();
      case CAMERA_STATES.PASS:
        return renderPassScreen();
      case CAMERA_STATES.CANCELLED:
        return renderCheckingCameraScreen();
      case CAMERA_STATES.READY:
      case CAMERA_STATES.STABLE_OK:
      case CAMERA_STATES.CAPTURING:
        return renderLivenessUI();
      case CAMERA_STATES.FAIL:
        // 🎯 Mostrar pantalla de resultado cuando hay fallo (solo si ya tenemos finalResult)
        return finalResult ? renderPassScreen() : renderLivenessUI();
      case CAMERA_STATES.AUTO_CLOSE:
        return renderPassScreen();
      default:
        return renderCheckingCameraScreen();
    }
  };
    function renderLoaderOrIcon() {
        if (showcheck) {
            return <img style={{ width: '49px', display: 'block' }} alt="result" id="result-image-ado" src={checkicon} />;
        } else if (isLuloProject) {
            return showLoader ? <LoaderLulo type={loadingMetaData ? 'v1' : 'v2'} /> : null;
        } else {
            return <LoaderIOS />;
        }
    }

    useEffect(() => {
        const wc = wcRef.current;
        const video = customVideoRef.current;

        if (!wc || !video) return;

        const handleStreamReady = async () => {
            try {
                if (cameraMounted.current) return
                // 🔥 Obtener el stream del componente IDRnD
                let stream = null;

                // Intentar diferentes métodos para obtener el stream
                if (wc.getVideoStream) {
                    stream = wc.getVideoStream();
                } else if (wc.stream) {
                    stream = wc.stream;
                } else if (mediaStreamRef.current) {
                    stream = mediaStreamRef.current;
                }

                if (stream) {
                    console.log('[Custom Video] Stream obtenido:', stream);

                    // Conectar al video personalizado
                    video.srcObject = stream;

                    // Guardar referencia
                    mediaStreamRef.current = stream;

                    // Asegurar que se reproduzca
                    await video.play();
                    cameraMounted.current = true;
                    console.log('[Custom Video] ✅ Video reproduciendo');
                } else {
                    console.warn('[Custom Video] ⚠️ No se pudo obtener el stream');
                }
            } catch (error) {
                console.error('[Custom Video] ❌ Error conectando stream:', error);
            }
        };

        // Escuchar cuando el stream está listo
        wc.addEventListener('ready', handleStreamReady);
        wc.addEventListener('stream-ready', handleStreamReady);
        wc.addEventListener('video-ready', handleStreamReady);

        // Intentar obtener el stream inmediatamente si ya está listo
        if (cameraState === CAMERA_STATES.READY ||
            cameraState === CAMERA_STATES.STABLE_OK) {
            handleStreamReady();
        }

        return () => {
            wc.removeEventListener('ready', handleStreamReady);
            wc.removeEventListener('stream-ready', handleStreamReady);
            wc.removeEventListener('video-ready', handleStreamReady);
        };
    }, [cameraState]);
    return (
        <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            backgroundColor: '#000',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '20px',
        }}>

            {(loading && emitReadyCam !== "true") && (
                <div className='loader-container'>
                    {renderLoaderOrIcon()}
                </div>
            )}

            {/* IDRnD - Oculto */}
            <idlive-face-capture
                ref={wcRef}
                debug="true"
                style={{
                    position: 'absolute',
                    opacity: 0,
                    width: '1px',
                    height: '1px',
                    pointerEvents: 'none',
                }}
            />

            {/* Mensaje */}
            <div style={{
                opacity: loading ? 0 : 1,
                backgroundColor: 'rgba(0,0,0,0.8)',
                padding: '16px 32px',
                borderRadius: '12px',
                position: 'absolute',
                top: '60px',
                zIndex: 100,
            }}>
                <p style={{
                    color: 'white',
                    margin: 0,
                    fontSize: '18px',
                    fontWeight: '600',
                }}>
                    {message}
                </p>
            </div>

            {/* Círculo con Video - SIEMPRE VISIBLE */}
            <div style={{
                opacity: loading ? 0 : 1,
                width: '300px',
                height: '400px',
                borderRadius: '50%',
                border: `6px solid ${borderColor}`,
                overflow: 'hidden',
                boxShadow: `0 0 40px ${borderColor}80`,
                transition: 'border-color 0.3s, box-shadow 0.3s',
                backgroundColor: '#1a1a1a',
                position: 'relative',
            }}>
                <video
                    ref={customVideoRef}
                    autoPlay
                    playsInline
                    muted
                    style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        transform: 'scaleX(-1)',
                        display: 'block',
                    }}
                />

                {/* Indicador de loading dentro del círculo */}
                {recordingVideo && (
                    <div style={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                    }}>
                        <div style={{
                            width: '40px',
                            height: '40px',
                            border: '4px solid rgba(16,185,129,0.2)',
                            borderTop: '4px solid #10B981',
                            borderRadius: '50%',
                            animation: 'spin 1s linear infinite',
                        }} />
                    </div>
                )}
            </div>

            <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
        </div>
    );
}
